// src/components/interactive/EmailSimulator.js - Interactive component demonstrating email encryption
import React, { useState } from 'react';
import styled from 'styled-components';
import Button from '../common/Button';
import { motion } from 'framer-motion';

const SimulatorContainer = styled.div`
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: var(--spacing-lg);
`;

const SimulatorHeader = styled.div`
  background-color: #0078d7;
  color: white;
  padding: var(--spacing-md) var(--spacing-lg);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SimulatorTitle = styled.h4`
  margin: 0;
  font-size: 1rem;
`;

const OutlookControls = styled.div`
  background-color: var(--background-light);
  padding: var(--spacing-sm) var(--spacing-lg);
  border-bottom: 1px solid var(--border-color);
  display: flex;
  gap: var(--spacing-md);
  color: var(--text-color);
`;

const EmailForm = styled.div`
  padding: var(--spacing-lg);
  background-color: var(--background-color);
  color: var(--text-color);
`;

const ControlGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  margin-bottom: var(--spacing-md);
`;

const Label = styled.label`
  font-weight: 500;
  color: var(--text-color);
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: var(--spacing-sm);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-family: var(--font-family);
  background-color: var(--background-color);
  color: var(--text-color);
  
  &:focus {
    outline: 2px solid var(--primary-color);
    outline-offset: 1px;
  }
`;

const TextArea = styled.textarea`
  padding: var(--spacing-sm);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-family: var(--font-family);
  min-height: 150px;
  background-color: var(--background-color);
  color: var(--text-color);
  
  &:focus {
    outline: 2px solid var(--primary-color);
    outline-offset: 1px;
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: var(--spacing-md);
  margin-top: var(--spacing-lg);
  justify-content: space-between;
`;

const MethodSelector = styled.div`
  display: flex;
  justify-content: center;
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
  border-bottom: 1px solid var(--background-light);
  padding-bottom: var(--spacing-md);
`;

const MethodButton = styled.button`
  background-color: ${props => props.active ? 'var(--primary-color)' : 'var(--background-light)'};
  color: ${props => props.active ? 'white' : 'var(--text-color)'};
  padding: var(--spacing-md) var(--spacing-lg);
  border-radius: var(--border-radius);
  border: none;
  cursor: pointer;
  font-weight: 600;
  min-width: 220px;
  transition: all var(--transition-speed) ease;

  &:hover {
    background-color: ${props => props.active ? 'var(--primary-dark)' : 'var(--background-dark)'};
  }
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  gap: var(--spacing-md);
  margin-top: var(--spacing-xl);
`;

const StepDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => props.active ? 'var(--primary-color)' : 'var(--border-color)'};
  transition: background-color var(--transition-speed) ease;
`;

const FeedbackMessage = styled(motion.div)`
  padding: var(--spacing-md);
  margin-top: var(--spacing-md);
  border-radius: var(--border-radius);
  color: white;
  background-color: ${props => props.type === 'success' ? 'var(--success-color)' : 'var(--error-color)'};
`;

const BestPractices = styled.div`
  margin-top: var(--spacing-lg);
  padding: var(--spacing-md);
  background-color: rgba(255, 185, 0, 0.1);
  border-left: 4px solid var(--accent-color);
  border-radius: var(--border-radius);
  color: var(--text-color);

  ul, ol {
    margin-top: var(--spacing-xs);
    margin-bottom: 0;
    padding-left: var(--spacing-xl);
  }

  li {
    margin-bottom: var(--spacing-xs);
    padding-left: var(--spacing-xs);
  }

  li:last-child {
    margin-bottom: 0;
  }

  ul li {
    list-style-type: disc;
  }

  ul ul, ol ul {
    margin-top: var(--spacing-xs);
  }
`;

const BestPracticesList = styled.ul`
  margin-top: var(--spacing-sm);
  margin-left: var(--spacing-xl);
`;

const RecipientInfo = styled.div`
  margin-top: var(--spacing-lg);
  padding: var(--spacing-lg);
  background-color: var(--background-light);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  color: var(--text-color);

  ul, ol {
    margin-top: var(--spacing-xs);
    margin-bottom: 0;
    padding-left: var(--spacing-xl);
  }

  li {
    margin-bottom: var(--spacing-xs);
    padding-left: var(--spacing-xs);
  }

  li:last-child {
    margin-bottom: 0;
  }

  ul li {
    list-style-type: disc;
  }
`;

const RecipientInfoTitle = styled.h4`
  margin-top: 0;
  margin-bottom: var(--spacing-md);
  color: var(--primary-color);
`;

const RecipientStep = styled.div`
  display: flex;
  margin-bottom: var(--spacing-md);
  gap: var(--spacing-md);
`;

const RecipientStepNumber = styled.div`
  min-width: 24px;
  height: 24px;
  background-color: var(--primary-color);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-weight: bold;
  font-size: 0.8rem;
`;

const RecipientStepContent = styled.div`
  flex: 1;
`;

const RecipientStepTitle = styled.div`
  font-weight: 600;
  margin-bottom: var(--spacing-xs);
`;

const RecipientStepDescription = styled.div`
  font-size: 0.9rem;
  line-height: 1.5;
`;

const EncryptionScreen = styled.div`
  position: relative;
  padding: var(--spacing-md);
  margin-top: var(--spacing-lg);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background-color: var(--background-light);
  color: var(--text-color);
`;

const PermissionOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  background-color: var(--background-color);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  margin-top: var(--spacing-md);
  color: var(--text-color);
`;

const PermissionOption = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm);
  cursor: pointer;
  &:hover {
    background-color: var(--background-light);
  }
`;

const AttachmentLabel = styled.div`
  background: #fffbe6;
  color: #b8860b;
  border: 1px solid #ffe58f;
  border-radius: var(--border-radius);
  padding: 6px 16px;
  font-size: 0.98rem;
  font-weight: 500;
  margin-bottom: var(--spacing-md);
  display: inline-block;
`;

export const EMAIL_SIMULATOR_INSTRUCTION =
  'This email contains Protected Health Information (PHI). You need to ensure the email is encrypted before sending.';

export const EmailEncryptionMethods = ({ method, setMethod }) => (
  <>
    <MethodSelector>
      <MethodButton 
        active={method === 1} 
        onClick={() => setMethod(1)}
      >
        Method 1: Manual Encryption
      </MethodButton>
      <MethodButton 
        active={method === 2} 
        onClick={() => setMethod(2)}
      >
        Method 2: Keyword Option
      </MethodButton>
    </MethodSelector>
    {method === 2 && (
      <BestPractices style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
        <strong style={{ fontSize: '1.1rem', display: 'block', marginBottom: 'var(--spacing-sm)' }}>Method 2: Keyword Option</strong>
        <BestPracticesList style={{ display: 'inline-block', textAlign: 'left' }}>
          <li>If sending PHI, include the letters "PHI" (without quotes) in your email subject line to automatically encrypt.</li>
          <li>For other confidential information, include "Encrypt" (without quotes) in your email subject line.</li>
        </BestPracticesList>
      </BestPractices>
    )}
    {method === 1 && (
      <BestPractices style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
        <strong style={{ fontSize: '1.1rem', display: 'block', marginBottom: 'var(--spacing-sm)' }}>Method 1: Manual Encryption (Outlook)</strong>
        <BestPracticesList style={{ display: 'inline-block', textAlign: 'left' }}>
          <li>Create a new email</li>
          <li>Go to Options &gt; Permission</li>
          <li>Select appropriate permission level:
            <ul>
              <li>"Encrypt" - Applies encryption</li>
              <li>"Do Not Forward" - Prevents forwarding, copying, printing</li>
            </ul>
          </li>
        </BestPracticesList>
      </BestPractices>
    )}
  </>
);

const EmailSimulator = ({ method, setMethod }) => {
  const [step, setStep] = useState(1);
  const [permissionVisible, setPermissionVisible] = useState(false);
  const [encryptionEnabled, setEncryptionEnabled] = useState(false);
  const [doNotForwardEnabled, setDoNotForwardEnabled] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [showRecipientInfo, setShowRecipientInfo] = useState(false);
  const [formData, setFormData] = useState({
    to: 'patient@example.com',
    subject: '',
    body: 'Dear Patient,\n\nI would like to discuss your recent lab results from 5/15/2023.\n\nYour cholesterol level was 240 mg/dL, which is above the recommended range.\n\nPlease call our office to schedule a follow-up appointment.\n\nBest regards,\nDr. Smith',
  });
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const togglePermissionMenu = () => {
    setPermissionVisible(!permissionVisible);
  };
  
  const toggleEncryption = () => {
    setEncryptionEnabled(!encryptionEnabled);
    if (permissionVisible) {
      setPermissionVisible(false);
    }
  };

  const toggleDoNotForward = () => {
    setDoNotForwardEnabled(!doNotForwardEnabled);
    if (permissionVisible) {
      setPermissionVisible(false);
    }
  };
  
  const handleSend = () => {
    if (method === 2) {
      // Keyword option method
      const subjectContainsPHI = formData.subject.includes('PHI');
      const subjectContainsEncrypt = formData.subject.includes('Encrypt');
      
      if (formData.body.includes('PHI') && !subjectContainsPHI) {
        setFeedback({
          type: 'error',
          message: 'Error: This email contains PHI. Include "PHI" in the subject line for automatic encryption.'
        });
      } else if (formData.body.includes('confidential') && !subjectContainsEncrypt) {
        setFeedback({
          type: 'error',
          message: 'Error: This email contains confidential information. Include "Encrypt" in the subject line for automatic encryption.'
        });
      } else if ((subjectContainsPHI || subjectContainsEncrypt)) {
        setFeedback({
          type: 'success',
          message: 'Success! The email has been automatically encrypted based on the subject line keyword.'
        });
        
        // Progress to the next step after a delay
        setTimeout(() => {
          setStep(step + 1);
          setFeedback(null);
        }, 2000);
      } else {
        setFeedback({
          type: 'error',
          message: 'Error: This email contains PHI but is not encrypted. Use appropriate subject line keywords.'
        });
      }
    } else {
      // Manual encryption method
      if (!encryptionEnabled) {
        setFeedback({
          type: 'error',
          message: 'Error: You need to manually encrypt this email since it contains PHI. Click on "Permission" then select "Encrypt".'
        });
      } else {
        setFeedback({
          type: 'success',
          message: 'Success! The email has been encrypted and sent securely.'
        });
        
        // Progress to the next step after a delay
        setTimeout(() => {
          setStep(step + 1);
          setFeedback(null);
        }, 2000);
      }
    }
  };
  
  const resetSimulator = () => {
    setStep(1);
    setEncryptionEnabled(false);
    setDoNotForwardEnabled(false);
    setPermissionVisible(false);
    setFeedback(null);
    setShowRecipientInfo(false);
    setFormData({
      to: 'patient@example.com',
      subject: '',
      body: 'Dear Patient,\n\nI would like to discuss your recent lab results from 5/15/2023.\n\nYour cholesterol level was 240 mg/dL, which is above the recommended range.\n\nPlease call our office to schedule a follow-up appointment.\n\nBest regards,\nDr. Smith',
    });
  };

  const getSubjectPlaceholder = () => {
    if (method === 2) {
      return 'Include "PHI" or "Encrypt" in subject for automatic encryption';
    }
    return 'Subject';
  };

  const toggleRecipientInfo = () => {
    setShowRecipientInfo(!showRecipientInfo);
  };
  
  // Simulate an attachment for demonstration (set to true to show the label)
  const hasAttachment = formData.body.toLowerCase().includes('attached') || formData.subject.toLowerCase().includes('attached');
  
  return (
    <SimulatorContainer>
      {/* Attachment label below instruction area, outside Outlook simulation */}
      {step === 1 && hasAttachment && (
        <AttachmentLabel>Attached: LabResults.pdf</AttachmentLabel>
      )}
      <SimulatorHeader>
        <SimulatorTitle>Outlook - New Message</SimulatorTitle>
        <div>Interactive Demo</div>
      </SimulatorHeader>
      
      <OutlookControls>
        <div>File</div>
        <div>Home</div>
        <div style={{ fontWeight: method === 1 ? 'bold' : 'normal' }}>Options</div>
        <div>Format</div>
        <div>Review</div>
      </OutlookControls>
      
      {step === 1 && (
        <>
          <EmailForm>
            <ControlGroup>
              <Label htmlFor="to">To:</Label>
              <Input 
                type="text" 
                id="to" 
                name="to" 
                value={formData.to} 
                onChange={handleInputChange} 
              />
            </ControlGroup>
            
            <ControlGroup>
              <Label htmlFor="subject">Subject:</Label>
              <Input 
                type="text" 
                id="subject" 
                name="subject" 
                value={formData.subject} 
                onChange={handleInputChange}
                placeholder={getSubjectPlaceholder()}
              />
            </ControlGroup>
            
            <ControlGroup>
              <TextArea 
                id="body" 
                name="body" 
                value={formData.body} 
                onChange={handleInputChange} 
              />
            </ControlGroup>
            
            <Toolbar>
              {method === 2 ? (
                <div>
                  <div>Subject line keywords for encryption: <strong>PHI</strong> or <strong>Encrypt</strong></div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 'var(--spacing-md)' }}>
                  <Button 
                    variant="secondary" 
                    onClick={togglePermissionMenu}
                  >
                    Permission ▾
                  </Button>
                  
                  {permissionVisible && (
                    <EncryptionScreen>
                      <div>Set permissions on this item:</div>
                      <PermissionOptions>
                        <PermissionOption onClick={toggleEncryption}>
                          <input 
                            type="radio" 
                            checked={encryptionEnabled && !doNotForwardEnabled} 
                            onChange={toggleEncryption} 
                            id="encrypt-radio"
                          />
                          <Label htmlFor="encrypt-radio" style={{ margin: 0 }}>Encrypt</Label>
                        </PermissionOption>
                        <PermissionOption onClick={toggleDoNotForward}>
                          <input 
                            type="radio" 
                            checked={doNotForwardEnabled} 
                            onChange={toggleDoNotForward} 
                            id="do-not-forward-radio"
                          />
                          <Label htmlFor="do-not-forward-radio" style={{ margin: 0 }}>Do Not Forward</Label>
                        </PermissionOption>
                        <PermissionOption onClick={() => {
                          setEncryptionEnabled(false);
                          setDoNotForwardEnabled(false);
                        }}>
                          <input 
                            type="radio" 
                            checked={!encryptionEnabled && !doNotForwardEnabled} 
                            onChange={() => {
                              setEncryptionEnabled(false);
                              setDoNotForwardEnabled(false);
                            }} 
                            id="no-permission-radio"
                          />
                          <Label htmlFor="no-permission-radio" style={{ margin: 0 }}>No permission set</Label>
                        </PermissionOption>
                      </PermissionOptions>
                    </EncryptionScreen>
                  )}

                  {(encryptionEnabled || doNotForwardEnabled) && (
                    <div style={{ display: 'flex', alignItems: 'center', color: 'var(--primary-color)' }}>
                      🔒 {doNotForwardEnabled ? 'Do Not Forward' : 'Encrypt'} enabled
                    </div>
                  )}
                </div>
              )}
              
              <Button onClick={handleSend}>
                Send
              </Button>
            </Toolbar>
            
            {feedback && (
              <FeedbackMessage 
                type={feedback.type}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {feedback.message}
              </FeedbackMessage>
            )}

            <div style={{ marginTop: 'var(--spacing-lg)', textAlign: 'center' }}>
              <Button variant="secondary" onClick={toggleRecipientInfo}>
                {showRecipientInfo ? 'Hide' : 'Show'} Recipient Experience Info
              </Button>
            </div>

            {showRecipientInfo && (
              <RecipientInfo>
                <RecipientInfoTitle>What Will Your Recipient Experience?</RecipientInfoTitle>
                <p>
                  When you send an encrypted email to an external recipient, they will go through 
                  the following steps to access your secure message:
                </p>
                
                <RecipientStep>
                  <RecipientStepNumber>1</RecipientStepNumber>
                  <RecipientStepContent>
                    <RecipientStepTitle>Notification Email</RecipientStepTitle>
                    <RecipientStepDescription>
                      The recipient receives a notification email with a "Read the message" button.
                      This email doesn't contain the actual PHI - it's just a notification.
                    </RecipientStepDescription>
                  </RecipientStepContent>
                </RecipientStep>
                
                <RecipientStep>
                  <RecipientStepNumber>2</RecipientStepNumber>
                  <RecipientStepContent>
                    <RecipientStepTitle>Authentication Options</RecipientStepTitle>
                    <RecipientStepDescription>
                      When they click the button, they're taken to a secure webpage where they can either:
                      <ul>
                        <li>Sign in with their Google/Microsoft/Yahoo account</li>
                        <li>Use a one-time passcode sent to their email</li>
                      </ul>
                    </RecipientStepDescription>
                  </RecipientStepContent>
                </RecipientStep>
                
                <RecipientStep>
                  <RecipientStepNumber>3</RecipientStepNumber>
                  <RecipientStepContent>
                    <RecipientStepTitle>One-Time Passcode</RecipientStepTitle>
                    <RecipientStepDescription>
                      If they choose the passcode option, they'll receive an email with a code that expires in 15 minutes.
                      They enter this code on the secure portal to access the message.
                    </RecipientStepDescription>
                  </RecipientStepContent>
                </RecipientStep>
                
                <RecipientStep>
                  <RecipientStepNumber>4</RecipientStepNumber>
                  <RecipientStepContent>
                    <RecipientStepTitle>View Secure Message</RecipientStepTitle>
                    <RecipientStepDescription>
                      After authenticating, they'll see your message in a secure web portal.
                      The message will show that it's encrypted and cannot be forwarded 
                      (if that option was selected).
                    </RecipientStepDescription>
                  </RecipientStepContent>
                </RecipientStep>
                
                <div style={{ marginTop: 'var(--spacing-md)' }}>
                  <p>
                    <strong>Note:</strong> For a more detailed walkthrough with screenshots of the entire recipient experience, 
                    check out the "Recipient Experience" tab in the Learning & Assessment section.
                  </p>
                </div>
              </RecipientInfo>
            )}
          </EmailForm>
        </>
      )}
      
      {step === 2 && (
        <EmailForm>
          <div style={{ textAlign: 'center' }}>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <span role="img" aria-label="success" style={{ fontSize: '4rem' }}>✅</span>
            </motion.div>
            
            <h3 style={{ marginTop: 'var(--spacing-lg)' }}>Email Successfully Encrypted and Sent!</h3>
            
            <p style={{ margin: 'var(--spacing-lg) 0' }}>
              You've successfully encrypted an email containing PHI using the {method === 2 ? 'keyword option' : 'manual encryption'} method. 
              {method === 2 
                ? ' The subject line keyword automatically applied encryption to your email.' 
                : ' You manually applied encryption settings to your email.'}
            </p>
            
            <BestPractices>
              <strong>Important Reminders:</strong>
              <BestPracticesList>
                <li>Avoid sending PHI via email whenever possible</li>
                <li>Use the minimum data necessary</li>
                <li>Double-check recipient's email address has a valid need-to-know</li>
                <li>Notify Security immediately if a mistake was made</li>
                <li>External recipients will receive a secure link and may need to use a one-time passcode that expires in 15 minutes</li>
              </BestPracticesList>
            </BestPractices>
            
            <RecipientInfo>
              <RecipientInfoTitle>Your Recipient's Next Steps</RecipientInfoTitle>
              
              <p>
                Now that you've sent an encrypted email, your recipient will:
              </p>
              
              <ol style={{ textAlign: 'left', lineHeight: 1.5 }}>
                <li>Receive a notification email indicating that you've sent them a protected message</li>
                <li>Click the "Read the message" button in the notification</li>
                <li>
                  Choose an authentication method:
                  <ul>
                    <li>Sign in with their existing account (Google, Microsoft, Yahoo, etc.)</li>
                    <li>Request a one-time passcode sent to their email</li>
                  </ul>
                </li>
                <li>View your encrypted message in a secure web browser interface</li>
              </ol>
              
              <p>
                Consider informing your recipient to expect this process, especially if it's their first time
                receiving an encrypted email. Let them know they may need to check their spam folder for
                the notification or passcode emails.
              </p>
              
              <div style={{ marginTop: 'var(--spacing-md)', textAlign: 'center' }}>
                <Button 
                  variant="secondary" 
                  onClick={() => window.location.href = '#/learning?tab=recipient'}
                >
                  View Detailed Recipient Experience
                </Button>
              </div>
            </RecipientInfo>
            
            <Button onClick={resetSimulator} style={{ marginTop: 'var(--spacing-xl)' }}>
              Try Again
            </Button>
          </div>
        </EmailForm>
      )}
      
      <StepIndicator>
        <StepDot active={step >= 1} />
        <StepDot active={step >= 2} />
      </StepIndicator>
    </SimulatorContainer>
  );
};

export default EmailSimulator; 
